import React, { useEffect, useRef } from "react"
import { View, StyleSheet } from "react-native"
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withTiming,
    withSequence,
    withSpring,
    withDelay,
    Easing,
    runOnJS,
} from "react-native-reanimated"
import { LinearGradient } from "expo-linear-gradient"
import { Petal } from "@common/components/icons/IconsM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"

/**
 * PetalRewardBadge - Clean petal + progress bar design
 * @param {boolean} isEarned - Whether the petal has been earned
 * @param {number} progress - Progress value (response count)
 * @param {Function} onEarnAnimationComplete - Callback when earn animation completes
 * @param {number} barWidth - Width of the progress bar (default: 80)
 */
const PetalRewardBadge = ({
    isEarned = false,
    progress = 0,
    onEarnAnimationComplete = () => {},
    barWidth = 80
}) => {
    const hasPlayedSound = useRef(false)

    // Animation values
    const progressWidth = useSharedValue(0)
    const petalScale = useSharedValue(1)
    const petalRotation = useSharedValue(0)
    const badgeScale = useSharedValue(1)
    const shimmerX = useSharedValue(-40)
    const textOpacity = useSharedValue(0)
    const textScale = useSharedValue(0.5)

    // Progress animation
    // Asymptotic approach: always progresses but never reaches 100% until isEarned
    // Uses formula: displayedProgress = 90 * (1 - e^(-progress * 0.5))
    // Gradual progression: 1st ~35%, 2nd ~57%, 3rd ~70%, 4th ~78%, 5th ~84%
    useEffect(() => {
        if (!isEarned) {
            // Asymptotic progress: gradually approaches 90%
            const asymptotic = 90 * (1 - Math.exp(-progress * 0.5))
            progressWidth.value = withTiming(asymptotic, {
                duration: 450,
                easing: Easing.out(Easing.cubic),
            })
            
            if (progress > 0) {
                petalScale.value = withSequence(
                    withTiming(1.2, { duration: 100 }),
                    withSpring(1, { damping: 10 })
                )
                shimmerX.value = -40
                shimmerX.value = withDelay(100, withTiming(barWidth + 40, { duration: 500 }))
            }
        }
    }, [progress, isEarned])

    // Celebration
    useEffect(() => {
        if (isEarned) {
            if (!hasPlayedSound.current) {
                hasPlayedSound.current = true
                PlaySoundM.play(require("../../../assets/sounds/petals-gained-2.mp3"))
            }
            
            progressWidth.value = withTiming(100, { duration: 200 })
            
            badgeScale.value = withSequence(
                withTiming(1.1, { duration: 150 }),
                withSpring(1, { damping: 8 })
            )
            
            petalScale.value = withSequence(
                withTiming(1.4, { duration: 150 }),
                withSpring(1.1, { damping: 6 }),
                withDelay(1500, withSpring(1, { damping: 10 }))
            )
            petalRotation.value = withSequence(
                withTiming(15, { duration: 80 }),
                withTiming(-15, { duration: 80 }),
                withSpring(0, { damping: 10 })
            )
            
            shimmerX.value = -40
            shimmerX.value = withTiming(barWidth + 40, { duration: 400 })
            
            textOpacity.value = withSequence(
                withDelay(100, withTiming(1, { duration: 150 })),
                withDelay(1500, withTiming(0, { duration: 200 }))
            )
            textScale.value = withSequence(
                withDelay(100, withSpring(1, { damping: 8 })),
                withDelay(1500, withTiming(0.8, { duration: 200 }))
            )
            
            setTimeout(() => runOnJS(onEarnAnimationComplete)(), 2000)
        }
    }, [isEarned])

    const badgeStyle = useAnimatedStyle(() => ({
        transform: [{ scale: badgeScale.value }],
    }))

    const petalStyle = useAnimatedStyle(() => ({
        transform: [
            { scale: petalScale.value },
            { rotate: `${petalRotation.value}deg` },
        ],
    }))

    const progressStyle = useAnimatedStyle(() => ({
        width: `${progressWidth.value}%`,
    }))

    const shimmerStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: shimmerX.value }],
    }))

    const textStyle = useAnimatedStyle(() => ({
        opacity: textOpacity.value,
        transform: [{ scale: textScale.value }],
    }))

    return (
        <Animated.View style={[styles.container, badgeStyle]}>
            <View style={styles.badge}>
                {/* Progress bar */}
                <View style={[styles.progressTrack, { width: barWidth }]}>
                    <Animated.View style={[styles.progressFillWrapper, progressStyle]}>
                        <LinearGradient
                            colors={["#F4A4A2", "#D3918F", "#B87A78"]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.progressFill}
                        />
                    </Animated.View>
                    
                    {/* Shimmer */}
                    <Animated.View style={[styles.shimmer, shimmerStyle]}>
                        <LinearGradient
                            colors={["transparent", "rgba(255,255,255,0.7)", "transparent"]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={StyleSheet.absoluteFill}
                        />
                    </Animated.View>
                </View>

                {/* Petal - on the right */}
                <Animated.View style={[styles.petalContainer, petalStyle]}>
                    <Petal size={26} />
                </Animated.View>
            </View>
            
            {/* +1 badge - only show when earned */}
            {isEarned && (
                <Animated.View style={[styles.plusBadge, textStyle]}>
                    <LinearGradient
                        colors={["#E8A5A3", "#D3918F", "#C07B79"]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.plusGradient}
                    >
                        <TextM fontWeight="ExtraBold" fontSize={13} color={theme.colors.white}>
                            +1
                        </TextM>
                    </LinearGradient>
                </Animated.View>
            )}
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: "center",
    },
    badge: {
        flexDirection: "row",
        alignItems: "center",
    },
    petalContainer: {
        marginLeft: 10,
    },
    progressTrack: {
        height: 8,
        backgroundColor: "#F5E6E5",
        borderRadius: 4,
        overflow: "hidden",
    },
    progressFillWrapper: {
        height: "100%",
        borderRadius: 4,
        overflow: "hidden",
    },
    progressFill: {
        flex: 1,
        borderRadius: 4,
    },
    shimmer: {
        position: "absolute",
        top: 0,
        width: 30,
        height: "100%",
    },
    plusBadge: {
        position: "absolute",
        bottom: -20,
        right: -8,
        borderRadius: 14,
        overflow: "hidden",
        shadowColor: "#D3918F",
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.4,
        shadowRadius: 6,
        elevation: 5,
    },
    plusGradient: {
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 14,
    },
})

export default PetalRewardBadge
